import * as React from "react"
import {
  type EntityCategory,
  useListCategoriesQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { CategoryTooltipContent } from "src/shared/components/Typeahead/CategoryTooltipContent"
import { createCategoryAncestorMap } from "src/shared/helpers/createCategoryAncestorMap"

interface Props {
  category: EntityCategory | undefined | null
  customTooltip?: React.ReactNode
  disabled?: boolean
}
export const useCategoryTooltipContent = ({ category, customTooltip, disabled }: Props) => {
  const categoryAncestorMap = useCategoryAncestorMap({
    category,
    customTooltip,
    disabled,
  })
  const breadcrumbString = categoryAncestorMap.get(category?.id || "")

  return React.useMemo(() => {
    switch (true) {
      case disabled:
        return null
      case Boolean(customTooltip):
        return customTooltip
      case Boolean(breadcrumbString):
        return (
          <CategoryTooltipContent category={category || undefined} subtext={breadcrumbString} />
        )
      default:
        return category?.name
    }
  }, [breadcrumbString, category, customTooltip, disabled])
}

function useCategoryAncestorMap({ category, customTooltip, disabled }: Props) {
  const skip = React.useMemo(
    () => Boolean(customTooltip || disabled || !category),
    [category, customTooltip, disabled]
  )

  const viewKey = useViewVersion()
  const response = useListCategoriesQuery({
    variables: { viewKey },
    skip,
  })

  const result = response.loading ? response.previousData : response.data

  return React.useMemo(
    () => (result ? createCategoryAncestorMap(result.listCategories) : new Map()),
    [result]
  )
}
